<template>
  <div class="strategy-guardrails">
    <!-- Cabecera de sección -->
    <div class="d-flex align-center justify-space-between mb-3">
      <div>
        <div class="d-flex align-center" style="gap: 6px;">
          <v-icon size="20" color="grey darken-1">mdi-shield-outline</v-icon>
          <span class="text-subtitle-1 font-weight-bold">
            {{ $t("guardrails") }}
          </span>
        </div>
        <div class="text-caption grey--text">
          {{ $t("guardrails_order_hint") }}
        </div>
      </div>
      <v-btn
        v-if="!readOnly && guardrailsEditable"
        color="secondary"
        small
        rounded
        @click="openPicker"
      >
        <v-icon size="20">mdi-plus</v-icon>
        {{ $t("add_guardrail") }}
      </v-btn>
    </div>

    <div ref="guardrailList">
      <div
        v-for="(guardrail, index) in guardrailRows"
        :key="guardrail.id"
        class="guardrail-row"
        :class="{ 'is-locked': guardrail.locked }"
        :data-guardrail-id="guardrail.id"
      >
        <div class="guardrail-body">
          <a-pricing-strategy-card-strategy-guardrail
            :guardrail="guardrail"
            :order-index="index"
            :read-only="readOnly"
            @edit-card="editGuardrail($event)"
            @delete-card="deleteGuardrail($event)"
          />
        </div>

        <!-- Controles de fila: subir / bajar -->
        <div
          v-if="!readOnly"
          class="guardrail-controls d-flex justify-center align-center flex-column"
        >
          <template v-if="!guardrail.locked">
            <v-btn
              color="grey"
              icon
              small
              :disabled="index === 0"
              :title="$t('guardrail_move_up')"
              @click="move(index, -1)"
            >
              <v-icon size="small">mdi-chevron-up</v-icon>
            </v-btn>
            <v-btn
              color="grey"
              icon
              small
              :disabled="index >= sortableCount - 1"
              :title="$t('guardrail_move_down')"
              @click="move(index, 1)"
            >
              <v-icon size="small">mdi-chevron-down</v-icon>
            </v-btn>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Bloque de guardarailes de una estrategia.
 *
 * Un guardarail se evalúa una vez calculado el cambio de precio: acota o
 * recoloca el resultado en lugar de generarlo. Ocupan toda la antelación, así
 * que aquí no hay grid de slots: es una lista vertical ordenada, y el orden de
 * arriba a abajo es el de evaluación.
 *
 * El último es siempre el fijo de precio mínimo/máximo, que llega marcado con
 * locked: no se puede borrar ni reordenar, pero sí editar.
 *
 * Emite `update` con el mismo contrato que aPricingStrategyGridRules:
 *   { delete: true, strategy_id, strategy_guardrail_id }
 *   { strategy_id, strategy_guardrail_id, rule_id, position }
 *   { reorder: true, strategy_id, guardrail_ids: [...] }
 */
import Sortable from "sortablejs";
import aPricingStrategyCardStrategyGuardrail from "@/components/pricing-strategy/aPricingStrategyCardStrategyGuardrail.vue";
import { mapState } from "vuex";

export default {
  name: "a-pricing-strategy-guardrails",
  components: {
    aPricingStrategyCardStrategyGuardrail
  },
  props: {
    strategy: {
      type: Object,
      required: true
    },
    readOnly: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    sortableInstance: null
  }),
  computed: {
    // Crear/editar guardarailes propios sí necesita los endpoints nuevos. El
    // fijo se ve siempre; el botón de añadir solo cuando el backend declara
    // soporte en /get-strategy-config.
    guardrailsEditable() {
      return !!(this.strategy_config && this.strategy_config.guardrail);
    },
    guardrailRows() {
      const rows = [...(this.strategy.strategyGuardrails || [])];
      // El fijo siempre al final, pase lo que pase con su position
      return rows.sort((a, b) => {
        if (a.locked !== b.locked) return a.locked ? 1 : -1;
        return a.position - b.position;
      });
    },
    // Cuántos se pueden reordenar (todos menos el fijo)
    sortableCount() {
      return this.guardrailRows.filter(g => !g.locked).length;
    },
    ...mapState({
      strategy_id: state => state.pricingstrategy.strategy_id,
      strategy_config: state => state.pricingstrategy.strategy_config
    })
  },
  watch: {
    guardrailRows() {
      this.$nextTick(this.initSortable);
    },
    // strategy_config llega por su propio fetch: si el soporte aparece
    // después de montarse el bloque, se piden entonces los datasets.
    guardrailsEditable(editable) {
      if (!editable) return;
      this.$store.dispatch("fetchPricingstrategyGuardrails");
      this.$store.dispatch("fetchPricingstrategyGuardrailTemplates");
    }
  },
  methods: {
    openPicker() {
      this.$store.dispatch("setPricingStrategyRuleTemplatesKind", "guardrail");
      this.$store.dispatch("setPricingStrategyRuleDefinitionMode", "guardrail");
      this.$store.dispatch("setPricingStrategyRuleTemplatesDialog", true);
    },
    // El fijo también se edita, pero solo en sus valores. `locked` viaja con
    // la regla para que el canvas bloquee la estructura (rutas y
    // condiciones); no contamina el payload, que se compone campo a campo.
    editGuardrail(guardrail) {
      this.$store.dispatch("setPricingStrategyRuleDefinitionMode", "guardrail");
      this.$store.dispatch("setPricingStrategyRuleToEdit", {
        ...guardrail.rule,
        locked: !!guardrail.locked
      });
      this.$store.dispatch("setPricingStrategyRuleDefinitionDialog", true);
    },
    deleteGuardrail(strategyGuardrailId) {
      this.$emit("update", {
        delete: true,
        strategy_id: this.strategy_id,
        strategy_guardrail_id: strategyGuardrailId
      });
    },
    // Reordenación con los botones subir/bajar
    move(index, delta) {
      const ids = this.guardrailRows.filter(g => !g.locked).map(g => g.id);
      const target = index + delta;
      if (target < 0 || target >= ids.length) return;
      const [moved] = ids.splice(index, 1);
      ids.splice(target, 0, moved);
      this.emitReorder(ids);
    },
    emitReorder(guardrailIds) {
      this.$emit("update", {
        reorder: true,
        strategy_id: this.strategy_id,
        guardrail_ids: guardrailIds
      });
    },
    initSortable() {
      if (this.readOnly) return;
      if (this.sortableInstance) this.sortableInstance.destroy();
      if (!this.$refs.guardrailList) return;
      this.sortableInstance = Sortable.create(this.$refs.guardrailList, {
        animation: 150,
        handle: ".guardrail-drag-handle",
        draggable: ".guardrail-row:not(.is-locked)",
        filter: ".is-locked",
        onEnd: this.onDragEnd
      });
    },
    onDragEnd(event) {
      const { oldIndex, newIndex, item, from } = event;
      if (oldIndex === newIndex) return;

      // Sortable ya ha movido el nodo. Se deshace para que el DOM lo vuelva a
      // pintar Vue desde los datos, y no queden dos fuentes de verdad.
      const reference = from.children[oldIndex];
      if (reference !== item) {
        from.insertBefore(
          item,
          oldIndex > newIndex ? reference : reference.nextSibling
        );
      }

      const ids = this.guardrailRows.filter(g => !g.locked).map(g => g.id);
      const [moved] = ids.splice(oldIndex, 1);
      ids.splice(newIndex, 0, moved);
      this.emitReorder(ids);
    }
  },
  mounted() {
    // Los guardarailes se piden aquí y no vía subscribed_modules: ese
    // mecanismo gatea el render de la pantalla entera y además dispara los
    // fetch siempre. Solo se piden si el backend declara soporte, para no
    // llamar a endpoints que todavía no existen: sin ellos el bloque sigue
    // mostrando el guardarail fijo, que sale de los settings del hotel.
    if (this.guardrailsEditable) {
      this.$store.dispatch("fetchPricingstrategyGuardrails");
      this.$store.dispatch("fetchPricingstrategyGuardrailTemplates");
    }
    this.$nextTick(this.initSortable);
  },
  beforeDestroy() {
    if (this.sortableInstance) {
      this.sortableInstance.destroy();
      this.sortableInstance = null;
    }
  }
};
</script>

<style lang="scss" scoped>
// Mismos valores que la grid de reglas, para que ambos bloques cuadren
$gray-medium: #f1f1f1;

.guardrail-header {
  padding: 0 10px;
  margin-bottom: 10px;
}

.guardrail-controls-spacer {
  width: 40px;
}

.guardrail-row {
  display: flex;
  // Más baja que la fila de la grid (135px): la card es única y a ancho
  // completo. El grafo se autoescala, así que no se recorta.
  min-height: 90px;
  padding: 0 10px;
  position: relative;
  margin-bottom: 10px;
  background: $gray-medium;
  border-radius: 10px;
}

.guardrail-body {
  flex: 1;
  position: relative;
  display: flex;
  align-items: stretch;
  padding: 10px 0;
}

.guardrail-controls {
  min-width: 40px;
}
</style>
